/**
 * Logger utility for production-safe logging
 * In production, this should be replaced with a proper logging service
 * like Sentry, LogRocket, or DataDog
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: string;
  stack?: string;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private logQueue: LogEntry[] = [];
  private maxQueueSize = 100;

  private write(level: LogLevel, message: string, data?: any) {
    const entry: LogEntry = {
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
    };

    // Capture stack trace for errors
    if (level === 'error' && data instanceof Error) {
      entry.stack = data.stack;
    }

    // In development, log to console
    if (this.isDevelopment) {
      const consoleMethod = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
      console[consoleMethod](`[${level.toUpperCase()}] ${message}`, data);
    }

    // Add to queue for potential remote logging
    this.addToQueue(entry);

    // In production, you would send to a logging service here
    // Example: this.sendToLoggingService(entry);
  }

  private addToQueue(entry: LogEntry) {
    this.logQueue.push(entry);

    // Keep queue size under control
    if (this.logQueue.length > this.maxQueueSize) {
      this.logQueue.shift();
    }
  }

  debug(message: string, data?: any) {
    this.write('debug', message, data);
  }

  info(message: string, data?: any) {
    this.write('info', message, data);
  }

  warn(message: string, data?: any) {
    this.write('warn', message, data);
  }

  error(message: string, error?: Error | any) {
    this.write('error', message, error);
  }

  log(message: string, ...details: unknown[]) {
    const data = details.length === 0 ? undefined : details.length === 1 ? details[0] : details;
    this.write('debug', message, data);
  }

  // Get recent logs for debugging
  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logQueue.slice(-count);
  }

  // Clear log queue
  clearLogs() {
    this.logQueue = [];
  }

  // Helper for API errors
  logApiError(endpoint: string, error: any) {
    const errorData = {
      endpoint,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
    };

    this.error(`API Error: ${endpoint}`, errorData);
  }

  // Helper for auth errors
  logAuthError(action: string, error: any) {
    this.error(`Auth Error: ${action}`, {
      action,
      error: error.message,
      code: error.code,
    });
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for testing
export { Logger };
